import axiosInstance from './axios';
import { DataType } from '../pages/tables/Reagents';
import { ExportFormat } from '../components/ExportTypes';
import { StandardType } from '../pages/tables/StandartSamples';

export const getReagents = async (laboratoryId: string): Promise<DataType[]> => {
    try {
        const response = await axiosInstance.get(`/api/laboratories/${laboratoryId}/reagents`);
        console.log('Response data:', response.data);
        
        if (!response.data) {
            console.error('No data received from server');
            return [];
        }
        
        // Преобразуем данные из БД в формат для таблицы
        return response.data.map((item: any, index: number): DataType => ({
            key: item.id,
            number: index + 1,
            receipt: {
                date: item.receipt_date,
                name: item.name,
                batch: item.batch,
                manufacturer: item.manufacturer,
                supplier: item.supplier,
                frequencyClass: item.frequency_class,
                expiryDate: item.expiration_date,
                expiryNotification: '',
                containerSize: item.quantity,
                containerUnit: item.unit,
                containerCount: 1,
                documents: item.has_documents,
                admission: item.has_admission,
            },
            storage: {
                isPrecursor: item.is_precursor,
                location: item.storage_location,
                conditions: item.storage_conditions,
            },
            accounting: {
                openedShelfLife: item.opened_shelf_life,
                used: 0,
                remaining: item.quantity,
                minimum_remaining: item.minimum_remaining || 10,
                notes: item.notes,
            },
            // Добавляем пользовательские колонки
            custom_columns: item.custom_columns || {}
        }));
    } catch (error) {
        console.error('Error fetching reagents:', error);
        throw error;
    }
}; 

export const createReagent = async (laboratoryId: string, data: any) => {
    try {
        // Выделяем кастомные поля из данных
        const { custom_fields, ...standardFields } = data;
        
        const response = await axiosInstance.post(
            `/api/laboratories/${laboratoryId}/reagents`,
            {
                ...standardFields,
                laboratory_id: parseInt(laboratoryId),
                // Преобразуем в число только если есть значение
                quantity: standardFields.quantity ? Number(standardFields.quantity) : null,
                opened_shelf_life: standardFields.opened_shelf_life ? parseInt(standardFields.opened_shelf_life) : null,
                receipt_date: standardFields.receipt_date || null,
                expiration_date: standardFields.expiration_date || null,
                // Добавляем кастомные поля
                custom_columns: custom_fields || {}
            }
        );
        return response.data;
    } catch (error: any) {
        if (error.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }
        throw error;
    }
}; 

export const takeReagent = async (
    laboratoryId: string,
    reagentId: number,
    quantity: number,
    research?: string
): Promise<{ success: boolean; remaining_quantity: number }> => {
    const response = await axiosInstance.post(
        `/api/take/${reagentId}`,
        {
            quantity,
            research
        }
    );
    return response.data;
}; 

export const deleteReagent = async (reagentId: number): Promise<void> => {
  try {
    await axiosInstance.delete(`/api/reagents/${reagentId}`);
  } catch (error: any) {
    if (error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw error;
  }
};

interface ReagentUpdateData {
  receipt_date?: string;
  name?: string;
  batch?: string;
  manufacturer?: string;
  supplier?: string;
  frequency_class?: string;
  expiration_date?: string;
  quantity?: number;
  unit?: string;
  has_documents?: boolean;
  has_admission?: boolean;
  is_precursor?: boolean;
  storage_location?: string;
  storage_conditions?: string;
  opened_shelf_life?: number;
  notes?: string;
  minimum_remaining?: number;
  custom_columns?: Record<string, any>; // Добавляем поле для кастомных колонок
}

export const updateReagent = async (reagentId: number, data: ReagentUpdateData): Promise<void> => {
  try {
    await axiosInstance.put(`/api/reagents/${reagentId}`, data);
  } catch (error: any) {
    if (error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw error;
  }
};

export const exportReagents = async (reagentIds: React.Key[], format: ExportFormat) => {
    try {
        const response = await axiosInstance.get(
            `/api/reagents/export?${reagentIds.map(id => `reagent_ids=${id}`).join('&')}&format=${format}`,
            {
                responseType: 'blob'
            }
        );
        
        // Создаем ссылку для скачивания
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `reagents.${format.toLowerCase()}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        return true;
    } catch (error) {
        console.error('Error exporting reagents:', error);
        throw error;
    }
};

export const getStandards = async (laboratoryId: number): Promise<StandardType[]> => {
    const response = await axiosInstance.get(`/api/laboratories/${laboratoryId}/standards`);
    return response.data.map((standard: any) => ({
        key: standard.id,
        number: standard.id,
        name: standard.name,
        manufacturer: standard.manufacturer,
        purpose: standard.purpose,
        batchCode: standard.batch_code,
        packaging: standard.packaging,
        indicator: standard.indicator,
        certifiedValue: standard.certified_value,
        certifiedValueUnit: standard.certified_value_unit,
        errorValue: standard.error_value,
        errorValueUnit: standard.error_value_unit,
        additionalInfo: standard.additional_info,
        normativeDocument: standard.normative_document,
        expirationDate: standard.expiration_date,
        expirationNotification: standard.expiration_notification,
        releaseDate: standard.release_date,
        notes: standard.notes,
        isPrecursor: standard.is_precursor,
        minimumRemaining: standard.minimum_remaining,
        
        // Поля хранения
        storageLocation: standard.storage_location,
        storageConditions: standard.storage_conditions,
        openedShelfLife: standard.opened_shelf_life,
        openedDate: standard.opened_date,
        
        // Поля учета
        used: standard.used || 0,
        remaining: standard.remaining || 0,
        
        custom_columns: standard.custom_columns || {}
    }));
};

export const createStandard = async (laboratoryId: string | number, data: FormData | Record<string, any>) => {
  try {
    let formData: FormData;

    // Если data уже является FormData, используем его как есть
    if (data instanceof FormData) {
      formData = data;
      console.log('=== RECEIVED FORM DATA ===');
      Array.from(formData.entries()).forEach(([key, value]) => {
        console.log(`${key}:`, value);
      });
      console.log('========================');
    } else {
      // Если получили обычный объект, создаем FormData
      formData = new FormData();
      
      // Выделяем кастомные поля и файл из данных
      const { custom_columns, normative_document_file, ...standardFields } = data;
      
      // Если есть файл, добавляем его
      if (normative_document_file) {
        formData.append('normative_document', normative_document_file);
      }
      
      // Создаем объект для преобразования в JSON-строку
      const standardData = {
        name: standardFields.name,
        manufacturer: standardFields.manufacturer || '',
        purpose: standardFields.purpose || '',
        batch_code: standardFields.batch_code || '',
        packaging: standardFields.packaging || '',
        indicator: standardFields.indicator || '',
        certified_value: standardFields.certified_value !== undefined ? Number(standardFields.certified_value) : null,
        certified_value_unit: standardFields.certified_value_unit || '',
        error_value: standardFields.error_value !== undefined ? Number(standardFields.error_value) : null,
        error_value_unit: standardFields.error_value_unit || '',
        additional_info: standardFields.additional_info || '',
        normative_document: standardFields.normative_document || '',
        expiration_date: standardFields.expiration_date || '',
        expiration_notification: standardFields.expiration_notification || '',
        release_date: standardFields.release_date || '',
        notes: standardFields.notes || '',
        is_precursor: Boolean(standardFields.is_precursor),
        minimum_remaining: Number(standardFields.minimum_remaining) || 10,
        storage_location: standardFields.storageLocation || '',  // camelCase -> snake_case
        storage_conditions: standardFields.storageConditions || '',  // camelCase -> snake_case
        opened_shelf_life: standardFields.openedShelfLife ? Number(standardFields.openedShelfLife) : null,  // camelCase -> snake_case
        opened_date: standardFields.openedDate || '',  // camelCase -> snake_case
        used: Number(standardFields.used) || 0,
        remaining: Number(standardFields.remaining) || 0,
        custom_columns: custom_columns || {}
      };

      console.log('=== STANDARD DATA ===');
      console.log(standardData);
      console.log('====================');
      
      // Добавляем JSON-строку в FormData
      formData.append('standard_data', JSON.stringify(standardData));
    }

    const response = await axiosInstance.post(
      `/api/laboratories/${laboratoryId}/standards`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw error;
  }
};

export const updateStandard = async (laboratoryId: number, standardId: number, data: FormData): Promise<any> => {
  try {
    const response = await axiosInstance.patch(
      `/api/laboratories/${laboratoryId}/standards/${standardId}`,
      data,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating standard:', error);
    throw error;
  }
};

export const deleteStandard = async (laboratoryId: string | number, standardId: number): Promise<void> => {
  try {
    await axiosInstance.delete(`/api/laboratories/${laboratoryId}/standards/${standardId}`);
  } catch (error: any) {
    if (error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw error;
  }
};

export const exportStandards = async (standardIds: React.Key[], format: ExportFormat) => {
    const response = await axiosInstance.get(`/api/standards/export`, {
        params: {
            standard_ids: standardIds,
            format: format
        },
        responseType: 'blob'
    });
    
    // Create a download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `standards_export.${format.toLowerCase()}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
};

export const getPrecursorGroups = async (laboratoryId: string): Promise<string[]> => {
    try {
        const response = await axiosInstance.get(`/api/laboratories/${laboratoryId}/precursor-groups`);
        return response.data;
    } catch (error) {
        console.error('Error fetching precursor groups:', error);
        throw error;
    }
};

export const getPrecursorCategories = async (laboratoryId: string): Promise<string[]> => {
    try {
        console.log('🌐 API: getPrecursorCategories called with laboratoryId:', laboratoryId);
        const url = `/api/laboratories/${laboratoryId}/precursor-categories`;
        console.log('🌐 API: making request to URL:', url);
        const response = await axiosInstance.get(url);
        console.log('🌐 API: response received:', response.data);
        return response.data;
    } catch (error) {
        console.error('🌐 API: Error fetching precursor categories:', error);
        throw error;
    }
};

export const getPrecursorGroupsByCategory = async (laboratoryId: string, category?: string): Promise<string[]> => {
    try {
        console.log('🌐 API: getPrecursorGroupsByCategory called with laboratoryId:', laboratoryId, 'category:', category);
        const params = category ? { category } : {};
        const url = `/api/laboratories/${laboratoryId}/precursor-groups-by-category`;
        console.log('🌐 API: making request to URL:', url, 'with params:', params);
        const response = await axiosInstance.get(url, { params });
        console.log('🌐 API: response received:', response.data);
        return response.data;
    } catch (error) {
        console.error('🌐 API: Error fetching precursor groups by category:', error);
        throw error;
    }
};

// Функция для загрузки нормативного документа
export const uploadNormativeDocument = async (
  laboratoryId: string | number,
  standardId: number,
  file: File
): Promise<{ filename: string; path: string }> => {
  try {
    const formData = new FormData();
    formData.append('standard_id', standardId.toString());
    formData.append('document', file);
    
    const response = await axiosInstance.post(
      `/api/laboratories/${laboratoryId}/standards/upload-document`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw error;
  }
};

// Функция для скачивания нормативного документа
export const downloadNormativeDocument = async (
  laboratoryId: string | number,
  standardId: number
): Promise<Blob> => {
  try {
    const response = await axiosInstance.get(
      `/api/laboratories/${laboratoryId}/standards/${standardId}/document`,
      {
        responseType: 'blob'
      }
    );
    
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw error;
  }
};

export const writeOffStandard = async (
    laboratoryId: string | number,
    batchCode: string,
    packaging: string,
    quantity: number,
    research?: string
): Promise<{ success: boolean; remaining_quantity: number }> => {
    const response = await axiosInstance.post(
        `/api/laboratories/${laboratoryId}/standards/write-off`,
        {
            batch_code: batchCode,
            packaging,
            quantity,
            research
        }
    );
    return response.data;
};

// Интерфейсы для прекурсоров
export interface PrecursorRegistryData {
    id: number;
    name: string;
    batch?: string;
    manufacturer?: string;
    category?: string;
    concentration?: string;
    location?: string;
    registration_number?: string;
    month?: string;
    initial_balance?: string;
    monthly_income?: string;
    total_with_balance?: string;
    monthly_expense?: string;
    final_balance?: string;
    actual_balance?: string;
    
    // Данные о приходе
    income_operation_number?: string;
    income_date?: string;
    income_document?: string;
    income_quantity?: string;
    income_responsible?: string;
    
    // Данные о расходе
    expense_operation_number?: string;
    expense_type?: string;
    expense_date?: string;
    expense_document?: string;
    expense_quantity?: string;
    expense_responsible?: string;
}

export interface PrecursorRegistryCreate {
    name: string;
    batch?: string;
    manufacturer?: string;
    category?: string;
    concentration?: string;
    location?: string;
    registration_number?: string;
    month?: string;
    initial_balance?: string;
    monthly_income?: string;
    total_with_balance?: string;
    monthly_expense?: string;
    final_balance?: string;
    actual_balance?: string;
    
    // Данные о приходе
    income_operation_number?: string;
    income_date?: string;
    income_document?: string;
    income_quantity?: string;
    income_responsible?: string;
    
    // Данные о расходе
    expense_operation_number?: string;
    expense_type?: string;
    expense_date?: string;
    expense_document?: string;
    expense_quantity?: string;
    expense_responsible?: string;
}

// API функции для прекурсоров
export const getPrecursorRegistries = async (laboratoryId: string, group?: string): Promise<PrecursorRegistryData[]> => {
    try {
        console.log('🌐 API: getPrecursorRegistries called with laboratoryId:', laboratoryId, 'group:', group);
        const params = group ? { group } : {};
        console.log('🌐 API: request params:', params);
        const response = await axiosInstance.get(`/api/laboratories/${laboratoryId}/precursors`, { params });
        console.log('🌐 API: getPrecursorRegistries response:', response.data.length, 'items');
        return response.data;
    } catch (error) {
        console.error('🌐 API: Error fetching precursor registries:', error);
        throw error;
    }
};

export const createPrecursorRegistry = async (
    laboratoryId: string, 
    data: PrecursorRegistryCreate
): Promise<PrecursorRegistryData> => {
    try {
        const response = await axiosInstance.post(`/api/laboratories/${laboratoryId}/precursors`, data);
        return response.data;
    } catch (error) {
        console.error('Error creating precursor registry:', error);
        throw error;
    }
};

export const updatePrecursorRegistry = async (
    laboratoryId: string,
    precursorId: number,
    data: Partial<PrecursorRegistryCreate>
): Promise<PrecursorRegistryData> => {
    try {
        const response = await axiosInstance.put(`/api/laboratories/${laboratoryId}/precursors/${precursorId}`, data);
        return response.data;
    } catch (error) {
        console.error('Error updating precursor registry:', error);
        throw error;
    }
};

export const deletePrecursorRegistry = async (
    laboratoryId: string,
    precursorId: number
): Promise<void> => {
    try {
        await axiosInstance.delete(`/api/laboratories/${laboratoryId}/precursors/${precursorId}`);
    } catch (error) {
        console.error('Error deleting precursor registry:', error);
        throw error;
    }
};